/**
 * Dynamic Performance Score Hook
 *
 * Calls the `compute_agent_performance()` RPC to calculate real metrics
 * from the leads table over a 30-day rolling window. Updates AgentContext
 * with the computed score, metrics, and tier so the UI reflects reality.
 *
 * Used by:
 * - Performance.tsx (on mount — shows real dashboard)
 * - LeadDetail.tsx (after agent_status changes — keeps score fresh)
 */

import { useCallback, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAgent } from "@/contexts/AgentContext";
import type { AgentProfile } from "@/contexts/AgentContext";

export interface PerformanceResult {
  performanceScore: number;
  avgResponseTime: number;
  contactRate: number;
  closeRate: number;
  totalLeadsInWindow: number;
  contactedLeads: number;
  wonLeads: number;
  tier: AgentProfile["tier"];
  // v2 fields — sub-scores, weights, and percentile
  responseTimeScore: number;
  contactRateScore: number;
  closeRateScore: number;
  responseTimeWeight: number;
  contactRateWeight: number;
  closeRateWeight: number;
  percentile: number;
  totalAgents: number;
}

const VALID_TIERS: AgentProfile["tier"][] = ["Bronze", "Silver", "Gold", "Elite"];

function parseTier(raw: string): AgentProfile["tier"] {
  if (VALID_TIERS.includes(raw as AgentProfile["tier"])) {
    return raw as AgentProfile["tier"];
  }
  return "Bronze";
}

export function useAgentPerformance() {
  const { state, actions } = useAgent();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<PerformanceResult | null>(null);

  const recalculate = useCallback(async (): Promise<PerformanceResult | null> => {
    const agentId = state.profile.id;
    if (!agentId) return null;

    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("compute_agent_performance", {
        p_agent_id: agentId,
      });

      if (error) {
        console.error("[useAgentPerformance] RPC failed:", error);
        setLoading(false);
        return null;
      }

      // The RPC returns a JSON object
      const raw = data as unknown as Record<string, unknown>;
      const parsed: PerformanceResult = {
        performanceScore: Number(raw.performanceScore ?? 0),
        avgResponseTime: Number(raw.avgResponseTime ?? 0),
        contactRate: Number(raw.contactRate ?? 0),
        closeRate: Number(raw.closeRate ?? 0),
        totalLeadsInWindow: Number(raw.totalLeadsInWindow ?? 0),
        contactedLeads: Number(raw.contactedLeads ?? 0),
        wonLeads: Number(raw.wonLeads ?? 0),
        tier: parseTier(String(raw.tier ?? "Bronze")),
        // v2 fields — gracefully default if RPC hasn't been upgraded yet
        responseTimeScore: Number(raw.responseTimeScore ?? 0),
        contactRateScore: Number(raw.contactRateScore ?? 0),
        closeRateScore: Number(raw.closeRateScore ?? 0),
        responseTimeWeight: Number(raw.responseTimeWeight ?? 30),
        contactRateWeight: Number(raw.contactRateWeight ?? 40),
        closeRateWeight: Number(raw.closeRateWeight ?? 30),
        percentile: Number(raw.percentile ?? 0),
        totalAgents: Number(raw.totalAgents ?? 1),
      };

      setResult(parsed);

      // Sync AgentContext so sidebar tier badge, Performance page, and routing all reflect the update
      actions.refreshPerformance({
        performanceScore: parsed.performanceScore,
        avgResponseTime: parsed.avgResponseTime,
        contactRate: parsed.contactRate,
        closeRate: parsed.closeRate,
        tier: parsed.tier,
      });

      setLoading(false);
      return parsed;
    } catch (err) {
      console.error("[useAgentPerformance] Error:", err);
      setLoading(false);
      return null;
    }
  }, [state.profile.id, actions]);

  return { recalculate, loading, result };
}
